import type { Container, Sprite } from 'pixi.js';
import { ballOf } from '../../sim/full/attacks/ball';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, paintSheet } from '../paint';
import { type Look, angleTo, fade } from '../player-look';
import type { Textures } from '../textures';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, hide, placeOutline, setTint } from '../util';
import {
  BALL_RADIUS,
  CLEATS,
  CREAM,
  HAIR,
  HANDS,
  NUMBER_AT,
  SHOES,
  SPORTIF_PIECES,
  STRIDE,
  UNIT,
} from './sportif-paint';

type Parts = Readonly<Record<keyof typeof SPORTIF_PIECES, Shape>>;

const EXTENT = 56;
const MIDDLE = 16;
const BALL_AT = { along: 18, across: 1.5, hop: 1.9, sway: 1.25, spin: 2 };
const KICK = { reach: 6, ticks: 2 };
const BOB_TICKS = 2;
const STRIDE_STANDING = { normal: 0.45, calm: 0.2 };
const SPRINT = { beats: 4, grow: 0.3, fade: 0.7, alpha: 0.75, calmAlpha: 0.35 };
const ELAN_GLOW = 14;
// No flicker sideways: the 10 turns over only once his head clearly points up or down the screen.
const NUMBER_FLIP = 0.2;

function createFootballerLook(
  bodies: Container,
  glows: Container,
  textures: Textures,
  parts: Parts,
): Look {
  const comet = add(glows, parts.streak, 1);
  const ballGlow = add(glows, textures.halo);
  const leftShoe = add(bodies, parts.shoe);
  const rightShoe = add(bodies, parts.shoe);
  const outline = add(bodies, parts.jersey);
  const jersey = add(bodies, parts.jersey);
  const number = add(bodies, parts.number);
  const leftHand = add(bodies, parts.hand);
  const rightHand = add(bodies, parts.hand);
  const head = add(bodies, parts.head);
  const hair = add(bodies, parts.hair);
  const band = add(bodies, parts.band);
  const ball = add(bodies, parts.ball);
  const body = [
    leftShoe,
    rightShoe,
    outline,
    jersey,
    number,
    leftHand,
    rightHand,
    head,
    hair,
    band,
  ];
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;
  let rolled = 0;
  let upsideDown = false;

  function put(sprite: Sprite, along: number, across: number, turn = 0): void {
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing + turn;
  }

  return {
    reset() {
      rolled = 0;
      upsideDown = false;
    },
    hide() {
      hide(...body, ball, ballGlow, comet);
    },
    place(input) {
      const { player, x, y, angle, scale, color, frame, state } = input;
      const { now, calm, palette, light, tempo } = frame;
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale;
      facing = angle;

      const beats = now / tempo.ticksPerBeat;
      const sprintTicks = SPRINT.beats * tempo.ticksPerBeat;
      const sprinting = input.sinceSkill >= 0 && input.sinceSkill < sprintTicks;
      const standing = calm ? STRIDE_STANDING.calm : STRIDE_STANDING.normal;
      const run = sprinting ? 1 : standing + (1 - standing) * input.moving;
      const pace = sprinting && !calm ? 2 : 1;
      const stride = Math.sin(Math.PI * beats * pace) * run;
      const kick = input.fireCount > 0 ? fade(input.sinceFire, KICK.ticks) : 0;
      const bob = fade(input.sinceBeat, BOB_TICKS) * (calm ? 0.35 : 1);

      for (const sprite of body) {
        sprite.visible = true;
      }
      put(leftShoe, STRIDE * stride, -SHOES.across);
      put(rightShoe, -STRIDE * stride + KICK.reach * kick, SHOES.across);
      for (const shoe of [leftShoe, rightShoe]) {
        shoe.scale.set(scale);
        setTint(shoe, CLEATS);
      }
      const wide = scale * (1 + 0.04 * bob);
      const short = scale * (1 - 0.03 * bob);
      put(jersey, 0, 0);
      jersey.scale.set(short, wide);
      setTint(jersey, color);
      placeOutline(outline, jersey, parts.jersey.texture, REFERENCE_RADIUS, frame);
      if (Math.abs(sin) > NUMBER_FLIP) {
        upsideDown = sin > 0;
      }
      put(number, NUMBER_AT, 0, upsideDown ? Math.PI : 0);
      number.scale.set(short, wide);
      setTint(number, CREAM);
      put(leftHand, HANDS.along - STRIDE * stride, -HANDS.across);
      put(rightHand, HANDS.along + STRIDE * stride, HANDS.across);
      for (const hand of [leftHand, rightHand]) {
        hand.scale.set(scale);
        setTint(hand, input.teint);
      }
      put(head, 0, 0);
      head.scale.set(scale);
      setTint(head, input.teint);
      put(hair, 0, 0);
      hair.scale.set(scale);
      setTint(hair, HAIR);
      put(band, 0, 0);
      band.scale.set(scale);
      setTint(band, CREAM);

      const owned = ballOf(state, player);
      ball.visible = owned === undefined || owned.phase === 'held';
      const full = player.gauge !== undefined && player.gauge >= 1;
      ballGlow.visible = ball.visible && full;
      rolled += (input.speed * player.speed * input.dt) / (BALL_RADIUS * UNIT * scale);
      if (ball.visible) {
        const hop = calm ? 0 : Math.sin(Math.PI * beats);
        const along = BALL_AT.along + BALL_AT.hop * Math.abs(hop) + KICK.reach * kick;
        const across = BALL_AT.across + BALL_AT.sway * hop;
        put(ball, along, across, rolled + (calm ? 0 : beats * BALL_AT.spin));
        ball.scale.set(scale);
        if (ballGlow.visible) {
          put(ballGlow, along, across);
          ballGlow.scale.set((ELAN_GLOW * scale * UNIT) / textures.halo.radius);
          ballGlow.alpha = Math.min(1, light.haloAlpha * (calm ? 1.2 : 1.2 + 0.4 * bob));
          setTint(ballGlow, palette.or);
        }
      }

      comet.visible = sprinting;
      if (sprinting) {
        const since = input.sinceSkill / tempo.ticksPerBeat;
        const shown =
          Math.min(1, since / SPRINT.grow) * Math.min(1, (SPRINT.beats - since) / SPRINT.fade);
        const along = input.moving > 0.5 ? angleTo(angle, input.heading) : 0;
        comet.position.set(x, y);
        comet.rotation = angle + along;
        comet.scale.set(shown * scale, scale);
        comet.alpha = (calm ? SPRINT.calmAlpha : SPRINT.alpha) * shown;
        setTint(comet, color);
      }
      return 0;
    },
  };
}

export const SPORTIF_DRAWING: ClassDrawing = {
  classId: 'sportif',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(SPORTIF_PIECES, density);
    return {
      extent: EXTENT,
      height: 1,
      centerAhead: MIDDLE,
      shadow: shapes.shadow,
      source,
      create: (bodies: Container, glows: Container, textures: Textures) =>
        createFootballerLook(bodies, glows, textures, shapes),
    };
  },
};
